import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export const useThemeStore = defineStore('theme', () => {
  // State
  const theme = ref(localStorage.getItem('theme') || 'light')

  // Getters
  const isDarkTheme = computed(() => theme.value === 'dark')
  const isLightTheme = computed(() => theme.value === 'light')

  // Actions
  function setTheme(newTheme) {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      console.warn(`Invalid theme: ${newTheme}. Using 'light' as fallback.`)
      newTheme = 'light'
    }

    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    applyTheme()
  }

  function toggleTheme() {
    const newTheme = theme.value === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }

  function applyTheme() {
    const html = document.documentElement
    const body = document.body

    if (theme.value === 'dark') {
      html.classList.add('dark-theme')
      body.classList.add('dark-theme')
      html.setAttribute('data-bs-theme', 'dark')
    } else {
      html.classList.remove('dark-theme')
      body.classList.remove('dark-theme')
      html.setAttribute('data-bs-theme', 'light')
    }
  }

  function detectSystemTheme() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  }

  function useSystemTheme() {
    const systemTheme = detectSystemTheme()
    setTheme(systemTheme)

    // Listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', (e) => {
        const newTheme = e.matches ? 'dark' : 'light'
        setTheme(newTheme)
      })
    }
  }

  // Initialize theme on store creation
  function initTheme() {
    // If no theme is saved, use system preference
    if (!localStorage.getItem('theme')) {
      const systemTheme = detectSystemTheme()
      theme.value = systemTheme
    }
    applyTheme()
  }

  // Watch for theme changes and apply them
  watch(theme, () => {
    applyTheme()
  })

  // Initialize immediately
  initTheme()

  return {
    // State
    theme,

    // Getters
    isDarkTheme,
    isLightTheme,

    // Actions
    setTheme,
    toggleTheme,
    useSystemTheme,
    detectSystemTheme,
    initTheme
  }
})
